import React from "react"
import useMetrics from "../../hooks/useMetrics.js"
import useExperiments from "../../hooks/useExperiments.js"
import useAggregates from "../../hooks/useAggregates.js"
import MetricStrip from "./overview/MetricStrip.jsx"
import HeroChart from "./overview/HeroChart.jsx"
import ProposalsBlock from "./overview/ProposalsBlock.jsx"
import ExperimentsList from "./overview/ExperimentsList.jsx"
import FrustrationPages from "./overview/FrustrationPages.jsx"
import ScriptMissingBanner from "./ScriptMissingBanner.jsx"
import ConversionMissingBanner from "./ConversionMissingBanner.jsx"

function shopLabel(shop) {
  if (!shop) return "your shop"
  try { return new URL(shop.url).host } catch { return shop.name || "your shop" }
}

export default function Overview({ t, shopState }) {
  const shop = shopState.shop
  const metricsState = useMetrics()
  const experimentsState = useExperiments()
  const aggregatesState = useAggregates()

  const metrics = metricsState.data
  const experiments = experimentsState.data || []
  const rate = metrics?.conversion_rate ?? 0
  const rateDelta = metrics?.conversion_rate_delta_pp
  const hasData = (metrics?.visits ?? 0) > 0

  const heroTitle = hasData
    ? t("dashboard.overview.hero_title_has_data", { rate: rate.toFixed(1) })
    : t("dashboard.overview.hero_title_empty")
  const heroLede = hasData
    ? t("dashboard.overview.hero_lede_has_data")
    : t("dashboard.overview.hero_lede_empty")

  const scriptInstalled = !!shop?.script_installed
  const conversionDefined = (shop?.conversion_rules || []).some((r) => r.active)

  return (
    <div className="space-y-10">
      {!conversionDefined && <ConversionMissingBanner t={t} />}
      {!scriptInstalled && <ScriptMissingBanner t={t} />}
      <header>
        <p
          className="font-mono text-[10px] text-ai mb-3"
          style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
        >
          {t("dashboard.overview.eyebrow_hero", { shop: shopLabel(shop).toUpperCase() })}
        </p>
        <div className="flex items-baseline gap-6 flex-wrap">
          <h1
            className="text-[68px] font-medium text-ink-primary"
            style={{ letterSpacing: "-0.045em", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}
          >
            {heroTitle}
          </h1>
          {hasData && rateDelta != null && rateDelta !== 0 && (
            <span
              className={`inline-flex items-center text-[13px] font-medium rounded-pill px-[10px] py-[3px]`}
              style={{
                background: rateDelta > 0 ? "#D1FAE5" : "#FEE2E2",
                color: rateDelta > 0 ? "#047857" : "#B91C1C",
                fontVariantNumeric: "tabular-nums"
              }}
            >
              {rateDelta > 0 ? "+" : "−"}{Math.abs(rateDelta).toFixed(1)}pp
            </span>
          )}
        </div>
        <p className="mt-3 text-sm text-ink-secondary max-w-2xl" style={{ lineHeight: 1.55 }}>
          {heroLede}
        </p>
      </header>

      <MetricStrip t={t} metrics={metrics} />
      <HeroChart t={t} aggregates={aggregatesState.data} />
      <ProposalsBlock t={t} experiments={experiments} onChange={experimentsState.refresh} />
      <ExperimentsList t={t} experiments={experiments} />
      <FrustrationPages t={t} pages={metrics?.frustration_pages} />
    </div>
  )
}
